import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Share2, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { SaidSections, SuggestionBox, MemberReflectionForm, AlignmentCompass } from "./ReflectionView";

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };
const label = (a) => (a.answer === "not_sure" ? "I'm not sure" : a.question_type === "textarea" ? a.answer : a.answer_label || String(a.answer));

function FollowUps({ entry, refresh }) {
  const [form, setForm] = useState({ tried: "", happened: "", helped: "", adjust: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try { await api.post(`/me/gauge/explorations/${entry.id}/followups`, form); setForm({ tried: "", happened: "", helped: "", adjust: "" }); toast.success("Added to your journey"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const fields = [["tried", "What did you try?"], ["happened", "What happened?"], ["helped", "What supported you, and what got in the way?"], ["adjust", "What would you continue, adjust, or explore next?"]];
  return (
    <div data-testid={`journey-followups-${entry.id}`}>
      <p className="font-serif text-lg text-[#1A242B]">What happened next</p>
      {(entry.followups || []).map((f) => (
        <div key={f.id} className="border-l-2 border-[#8FA89B] pl-3 my-3 text-sm text-[#4A5D6B]" data-testid={`journey-followup-${f.id}`}>
          <p className="text-xs text-[#8FA89B]">{fmt(f.created_at)}</p>
          {fields.map(([k, l]) => f[k] && <p key={k}><b className="text-[#1A242B]">{l}</b> {f[k]}</p>)}
        </div>
      ))}
      <div className="grid sm:grid-cols-2 gap-2 mt-2">
        {fields.map(([k, l]) => <label key={k} className="text-xs text-[#4A5D6B]">{l}<textarea rows={2} value={form[k]} onChange={set(k)} data-testid={`followup-${k}-${entry.id}`} className="eyw-input w-full mt-1 resize-none text-sm" /></label>)}
      </div>
      <button onClick={save} disabled={busy} data-testid={`followup-save-${entry.id}`} className="eyw-btn-outline !py-2 text-sm mt-2">{busy && <Loader2 size={14} className="animate-spin" />} Add reflection</button>
    </div>
  );
}

function SharePicker({ entry, refresh }) {
  const { data: options = [] } = useQuery({ queryKey: ["gauge-share-options"], queryFn: () => api.get("/me/gauge/share-options").then((r) => r.data) });
  const shared = entry.shared_with || [];
  const toggle = async (id) => {
    const ids = shared.includes(id) ? shared.filter((x) => x !== id) : [...shared, id];
    try { await api.put(`/me/gauge/explorations/${entry.id}/share`, { enhancer_ids: ids }); toast.success(ids.includes(id) ? "Shared" : "Sharing stopped"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid={`journey-share-${entry.id}`}>
      <p className="font-serif text-lg text-[#1A242B] flex items-center gap-2"><Share2 size={16} /> Share with an enhancer</p>
      <p className="text-xs text-[#4A5D6B] mb-2">Private unless you choose. Sharing lets an enhancer you work with read this exploration, your reflection and follow-ups, so they can support you better. You can stop sharing anytime.</p>
      {options.length === 0 ? <p className="text-xs text-[#8FA89B] flex items-center gap-1.5"><Lock size={12} /> After you book with an enhancer, they'll appear here.</p> : (
        <div className="flex flex-wrap gap-2">
          {options.map((o) => {
            const on = shared.includes(o.id);
            return <button key={o.id} onClick={() => toggle(o.id)} aria-pressed={on} data-testid={`journey-share-${entry.id}-${o.id}`}
              className={`px-3 py-1.5 rounded-full border text-sm ${on ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>{on ? "Shared with " : ""}{o.name}</button>;
          })}
        </div>
      )}
    </div>
  );
}

export function EntryAnswers({ entry }) {
  const all = [...(entry.answers || []), ...(entry.deeper_answers || [])];
  if (!all.length) return <p className="text-xs text-[#8FA89B]">Answers weren't saved for explorations taken before this was added.</p>;
  return (
    <ul className="space-y-1.5 text-sm" data-testid={`journey-answers-${entry.id}`}>
      {all.map((a) => <li key={a.id}><span className="text-[#8FA89B] text-xs block">{a.question_text}</span><span className="text-[#1A242B]">{label(a)}</span></li>)}
    </ul>
  );
}

// Expandable detail for one saved exploration: reflection, my intention, answers, follow-ups, sharing.
export default function JourneyEntryDetail({ entry }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["my-gauge-journey"] });
  return (
    <div className="mt-3">
      <button onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`journey-open-${entry.id}`} className="text-sm text-[#144F75] inline-flex items-center gap-1">
        {open ? "Hide details" : "Open reflection, answers and follow-ups"} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-4 space-y-6 border-t border-[#E5E0D8] pt-4" data-testid={`journey-detail-${entry.id}`}>
          {entry.reflection ? (
            <>
              <AlignmentCompass said={entry.reflection.said} />
              <SaidSections said={entry.reflection.said} />
              <SuggestionBox suggestion={entry.reflection.suggestion} />
            </>
          ) : <p className="text-sm text-[#8FA89B]">No written reflection for this exploration.</p>}
          <MemberReflectionForm explorationId={entry.id} initial={entry.member_reflection || {}} suggestions={entry.reflection?.suggestion?.next_steps || []} onSaved={refresh} />
          <details><summary className="text-sm text-[#144F75] cursor-pointer" data-testid={`journey-answers-toggle-${entry.id}`}>All answers ({fmt(entry.created_at)})</summary><div className="mt-3"><EntryAnswers entry={entry} /></div></details>
          <FollowUps entry={entry} refresh={refresh} />
          <SharePicker entry={entry} refresh={refresh} />
        </div>
      )}
    </div>
  );
}

// Side-by-side comparison of two explorations, matching questions asked in both.
export function CompareView({ a, b }) {
  const map = (e) => Object.fromEntries([...(e.answers || []), ...(e.deeper_answers || [])].map((x) => [x.id, x]));
  const ma = map(a); const mb = map(b);
  const common = Object.keys(ma).filter((k) => mb[k]);
  const col = (e) => (
    <div className="space-y-1 text-sm">
      <p className="font-semibold text-[#1A242B]">{fmt(e.created_at)} · {e.path_title}</p>
      <p className="text-[#4A5D6B]">{(e.top_focus_areas || []).join(", ")}</p>
      {e.member_reflection?.intention && <p><b>Intention:</b> {e.member_reflection.intention}</p>}
      {e.member_reflection?.chosen_next_step && <p><b>Next step:</b> {e.member_reflection.chosen_next_step}</p>}
    </div>
  );
  return (
    <div className="eyw-card p-5 mb-8" data-testid="journey-compare">
      <p className="eyw-label mb-3">Then and now</p>
      <div className="grid sm:grid-cols-2 gap-4 pb-4 border-b border-[#E5E0D8]">{col(b)}{col(a)}</div>
      {common.length === 0 ? <p className="text-sm text-[#8FA89B] mt-3">These two explorations didn't share questions, so there's nothing to line up side by side.</p> : (
        <ul className="divide-y divide-[#E5E0D8]">
          {common.map((k) => (
            <li key={k} className="py-3">
              <p className="text-xs text-[#8FA89B] mb-1">{ma[k].question_text}</p>
              <div className="grid sm:grid-cols-2 gap-4 text-sm text-[#1A242B]"><span>{label(mb[k])}</span><span>{label(ma[k])}</span></div>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-[#8FA89B] mt-3">Patterns here are yours to interpret. Nothing is a verdict.</p>
    </div>
  );
}
